/* global GM_STATE */
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import { fetchData, postData } from 'helpers/fetch'
import { addClass, select, selectAll, removeClass } from 'helpers/dom'
import { hasOwnProperties } from 'helpers/utils'
import { getCustomBadgeClass } from 'libs/shopify'
import { getChildBundleItemsBySKU } from 'hooks/useStorefront'

const selectors = {
  CART_COUNT_SELECTOR: '#CartCount',
  MINI_CART_CONTENT_CLASS: '.js-mini-cart-content'
}

// Get settings dynamically to ensure GM_STATE is loaded
const settings = GM_STATE
const customBadgeTags = settings?.customs?.badge?.tags || []
const customBadgeItems = settings?.customs?.badge?.items || []

const cartError = async (response) => {
  const error = await response.json()
  globalEmitter.emit(emitterProps.notice.global, {
    type: 'error',
    content: error.description
  })
  throw new Error(error.description)
}

const getNativeCart = () => {
  return fetchData('/cart.js', cartError).then((cart) => cart)
}

const getCart = () => {
  return fetchData('/cart?view=ajax', cartError).then((cart) => {
    if (cart?.count) {
      globalEmitter.emit(emitterProps.cart.count, cart.count)
    }
    return cart
  })
}

const getLineItem = (id, properties, quantity) => {
  const data = {
    items: [
      {
        id,
        properties,
        quantity
      }
    ]
  }
  return data
}

const addCartItem = (id, properties = {}, quantity = 1) => {
  const lineItem = getLineItem(id, properties, quantity)
  return postData('/cart/add.js', lineItem, 'json', cartError).then((item) => {
    return item
  })
}

const addMultipleCartItems = (items) => {
  return postData('/cart/add.js', items, 'json', async (response) => {
    const { description } = await response.json()
    globalEmitter.emit(emitterProps.notice.global, {
      type: 'error',
      content: description
    })
  }).then((cart) => {
    if (cart?.item_count) {
      globalEmitter.emit(emitterProps.cart.change, cart.item_count)
    }
    globalEmitter.emit(emitterProps.cart.add, cart)
    return cart
  })
}

const updateMultipleCartItems = (items) => {
  return postData('/cart/update.js', items, 'json', async (response) => {
    const { description } = await response.json()
    globalEmitter.emit(emitterProps.notice.global, {
      type: 'error',
      content: description
    })
  }).then((items) => {
    return items
  }).catch((error) => cartError(error))
}

const updateCartItem = (id, quantity) => {
  return postData('/cart/change.js', {
    id,
    quantity
  }, 'json', cartError).then((cart) => {
    globalEmitter.emit(emitterProps.cart.change, cart.item_count)
    return cart
  })
}

const changeQuantity = (id, quantity) => {
  return postData('/cart/change.js', {
    id,
    quantity
  }, 'json', cartError).then((cart) => {
    return cart
  })
}

const removeCartItem = (id) => {
  return postData('/cart/change.js', {
    id,
    quantity: 0
  }, 'json', cartError).then((cart) => {
    if (cart?.count) {
      globalEmitter.emit(emitterProps.cart.change, cart.item_count)
    }
    return cart
  })
}

const clearCart = () => {
  return postData('/cart/clear.js', {}, 'json', cartError).then((cart) => {
    globalEmitter.emit(emitterProps.cart.clear, cart)
    return cart
  })
}

const reRenderCartCount = (count) => {
  const els = selectAll(selectors.CART_COUNT_SELECTOR, document.body)

  if (els) {
    if (count === 0) {
      els.forEach((el) => addClass('hidden', el))
    } else {
      els.forEach((el) => {
        el.innerText = count
        removeClass('hidden', el)
        addClass('flex', el)
      })
    }
  }
}

const updateCart = async () => {
  const newCart = await getCart()
  if (cart) {
    reRenderCartCount(newCart.item_count)
  }
  return newCart
}

const syncPreorderCartProperties = async (cartItems) => {
  for (const item of cartItems) {
    const props = item.properties || {}

    const onHandStock = Number(item.mfield_custom_onhandstock) || 0
    const futureStock = Number(item.mfield_custom_futurestock) || 0
    const preorderDateFromLiquid = item.preorder_date || ''

    let isPreorder = (onHandStock <= 0) && (futureStock > 0)
    let finalPreorderDate = preorderDateFromLiquid

    const hasPreorderFlag = props.preorder === 'Preorder'
    const currentPODate = props._preorderDate || ''

    // Check if the item is a bundle
    if (item.is_bundle === 'true' && item.bundle_child_skus) {
      // If the item is a bundle, we need to get the preorder dates of its child items
      const childSKUs = item.bundle_child_skus.split(',').map(s => s.split('#')[0].trim())

      const childProducts = await getChildBundleItemsBySKU(childSKUs)
        .then(res => res.ok && res.json ? res.json() : Promise.reject(res))
        .then(body => {
          if (body.errors) throw body
          return body.data.search.edges.map(x => x.node).filter(Boolean)
        })
        .catch(err => {
          console.error('⚠️ getChildStockStatus failed', err)
          return []
        })

      // Filter out products that don't match the child product SKUs
      const filteredBundleProducts = childProducts.filter((prod) =>
        prod.variants.edges.some((variant) => childSKUs.includes(variant.node.sku))
      )

      const preorderDates = []

      filteredBundleProducts.forEach(product => {
        product.variants.edges.forEach(variant => {
          const v = variant.node
          if (!childSKUs.includes(v.sku)) return

          const onHand = Number(v.onHandStock?.value || 0)
          const future = Number(v.futureStock?.value || 0)
          const poDate = v.preOrderDate?.value

          // Collect preorder dates if the child item is a preorder
          if (onHand <= 0 && future > 0 && poDate) {
            preorderDates.push(new Date(poDate))
          }
        })
      })

      if (preorderDates.length > 0) {
        isPreorder = true

        const latestDate = new Date(Math.max(...preorderDates.map(d => d.getTime())))
        finalPreorderDate = latestDate.toLocaleDateString('en-AU', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        })
      } else {
        isPreorder = false
        finalPreorderDate = ''
      }
    }

    if (isPreorder) {
      if (!hasPreorderFlag || currentPODate !== finalPreorderDate) {
        // Add or update preorder properties if needed
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: item.key,
            quantity: item.quantity,
            properties: {
              ...props,
              _preorderDate: finalPreorderDate
            }
          })
        })
      }
    } else {
      // Remove preorder properties if item is no longer preorder
      if (hasPreorderFlag || currentPODate) {
        await removeCartItem(item.key)
        const cleanedProps = { ...props }
        delete cleanedProps.preorder
        delete cleanedProps._preorderDate

        await addCartItem(item.variant_id, cleanedProps, item.quantity)
      }
    }
  }
}

const forceReloadItem = async (item) => {
  const currentQty = item.quantity
  const nextQty = item.quantity + 1
  await changeQuantity(item.key, nextQty)
  const cart = await changeQuantity(item.key, currentQty)
  return cart
}

const openMiniCart = () => {
  const el = select('cart-slideout modal-dialog', document.body)
  if (el) {
    el.show()
    const contentEl = select(selectors.MINI_CART_CONTENT_CLASS, el)
    if (contentEl) {
      contentEl.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      })
    }
  }
}

const getCartInfo = (item) => {
  const arrs = []
  if (item.product_title) {
    arrs.push({
      name: getProductSubtitle(item.product_title),
      key: null
    })
  }
  return arrs
}

const getCartProperties = (items) => {
  const arrs = []
  if (items && Object.keys(items).length > 0) {
    for (const [key, value] of Object.entries(items)) {
      arrs.push({
        name: key,
        key: value
      })
    }
  }
  return arrs
}

const getDisableChangeQuantity = (properties) => {
  return false
}

const getItemsByKey = (items, item, key) => items.filter((cartItem) => cartItem.properties && hasOwnProperties(key, cartItem.properties) && cartItem.properties.timestamp === item.properties.timestamp)

const getMaxQuantity = (cartItem) => {
  return cartItem.inventory_management === 'shopify' && cartItem.inventory_policy === 'deny' && cartItem.inventory_quantity > 0
    ? cartItem.inventory_quantity
    : 1000
}

const removeBundlesItem = async (item) => {
  const newCart = await updateCart()
  const bundleItems = getItemsByKey(newCart.items, item, 'timestamp')
  if (bundleItems.length === 0) {
    return
  }
  const newUpdates = {}
  for (const itemCart of bundleItems) {
    newUpdates[itemCart.id] = 0
  }
  const body = {
    updates: newUpdates
  }
  await updateMultipleCartItems(body)
}

const getMaxQuantityByVariant = (cartItems, variant) => {
  if (!cartItems.length) return variant.inventory_quantity

  const currentItem = cartItems.find((item) => item.id === variant.id)

  if (variant.inventory_policy === 'continue') return 9999

  if (!currentItem) return variant.inventory_quantity

  const gap = variant.inventory_quantity - currentItem.quantity

  return gap
}

const klaviyoTrackingAddToCart = (newCart) => {
  const _learnq = window._learnq || []

  let cart = {
    total_price: newCart.total_price / 100,
    $value: newCart.total_price / 100,
    total_discount: newCart.total_discount,
    original_total_price: newCart.original_total_price / 100,
    items: newCart.items
  }

  if (window.item !== 'undefined') {
    cart = Object.assign(cart, window.item)
  }

  _learnq.push(['track', 'Added to Cart', cart])
}

export {
  getNativeCart,
  getCart,
  addCartItem,
  updateCartItem,
  removeCartItem,
  clearCart,
  updateCart,
  openMiniCart,
  getCartInfo,
  getCartProperties,
  addMultipleCartItems,
  getDisableChangeQuantity,
  forceReloadItem,
  updateMultipleCartItems,
  getItemsByKey,
  getMaxQuantity,
  removeBundlesItem,
  reRenderCartCount,
  getMaxQuantityByVariant,
  klaviyoTrackingAddToCart,
  syncPreorderCartProperties
}
